// ── CFT (Solid Wood Issue Sheet) — print document, MOZI template (mozPrint.js) ──
import {
  esc, fmtDate, heroHtml, tabHtml, detailsHtml, signoffHtml, buildMozDocument, brandWordOf,
} from './mozPrint';

import { anyCft as rowCft, isAllRow } from '../../utils/cft';

const f4 = n => Number(n || 0).toFixed(4);

const GROUPS = [['demand', 'Demand'], ['issue', 'Issue'], ['return', 'Return']];

export function buildCftHtml(sheet, rows, totals, photos = [], settings = {}, origin = window.location.origin) {
  const STATUS = { draft: 'Draft', pending: 'Waiting for Approval', approved: 'Approved', submitted: 'Submitted' };

  const details = [
    ['CFT Sheet No.', sheet.sheet_no, ' big'],
    ['Date', fmtDate(sheet.created_at)],
    ['Project Name', sheet.project_name],
    ['Project Code', sheet.project_code],
    ['Product', sheet.item_name],
    ['Proto Code', sheet.item_proto_code],
    ['Wood Type', sheet.wood_type],
    ['Prepared By', sheet.created_by_name],
  ];

  const groupTable = (type, label, total) => {
    const list = rows.filter(r => r.row_type === type);
    const body = list.map((r, i) => {
      const c = rowCft(r);
      const all = isAllRow(r);
      return `<tr>
        <td class="c sn">${i + 1}</td>
        <td>${esc(r.label || '')}</td>
        ${all ? '<td class="n">—</td><td class="n">—</td><td class="n b">ALL CFT</td>'
    : `<td class="n">${esc(Number(r.length) || 0)}</td><td class="n">${esc(Number(r.width) || 0)}</td><td class="n">${esc(Number(r.thickness) || 0)}</td>`}
        <td class="c qty">${esc(Number(r.qty) || 0)}</td>
        <td class="n">${all ? '—' : f4(c.gross)}</td><td class="n b">${f4(c.net)}</td>
      </tr>`;
    }).join('');
    return `
    ${tabHtml('box', `${label} (${list.length})`, '52mm')}
    <table class="items cft">
      <thead><tr>
        <th class="c sn">S.No.</th><th>Label / Part</th><th class="n">L (mm)</th><th class="n">W (mm)</th><th class="n">T (mm)</th>
        <th class="c">Qty.</th><th class="n">CFT / pc</th><th class="n">Total CFT</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="none">No ${label.toLowerCase()} rows</td></tr>`}</tbody>
      <tfoot><tr><td colspan="7" class="tl">Total ${label} CFT</td><td class="n b">${f4(total)}</td></tr></tfoot>
    </table>`;
  };

  const netCls = totals.net > 0 ? 'excess' : totals.net < 0 ? 'shortage' : '';
  const netWord = totals.net > 0 ? ' (Excess)' : totals.net < 0 ? ' (Shortage)' : '';
  const sumRow = (k, v, cls = '') => `<tr class="${cls}"><td>${k}</td><td class="n">${f4(v)}</td></tr>`;

  const body = `
  ${heroHtml({ brandWord: brandWordOf(settings), titleTop: 'Solid Wood', titleAccent: 'Issue Sheet (CFT)', sub: STATUS[sheet.status] || 'Draft' })}

  ${tabHtml('doc', 'Sheet Details', '56mm')}
  ${detailsHtml(details)}

  ${groupTable('demand', 'Demand', totals.demand)}
  ${groupTable('issue', 'Issue', totals.issue)}
  ${groupTable('return', 'Return', totals.ret)}

  <div class="sum-wrap">
  ${tabHtml('check', 'CFT Summary', '50mm')}
  <table class="sumt">
    <tbody>
      ${sumRow('Total Demand CFT', totals.demand)}
      ${sumRow('Offcut (30%)', totals.offcut)}
      ${sumRow('<b>Total Required CFT (Demand + 30%)</b>', totals.required, 'strong')}
      ${sumRow('Total Issue CFT', totals.issue)}
      ${sumRow('Difference (Issue − Required)', totals.difference)}
      ${sumRow('Total Return CFT', totals.ret)}
      ${totals.consumption !== undefined ? sumRow('Net Consumption (Issue − Return)', totals.consumption) : ''}
      ${sumRow(`Net Difference${netWord}`, totals.net, `net ${netCls}`)}
    </tbody>
  </table>
  <div class="formula">CFT = L × W × T × 0.00000003528 (per piece) × Qty · Offcut fixed at 30% · Difference = Issue − Required · Net = Difference − Return</div>
  </div>

  ${(sheet.tally_rows || []).length ? `
  <div class="sum-wrap">
    ${tabHtml('doc', 'Tally Entry', '46mm')}
    <table class="sumt tally">
      <tbody>
        <tr class="th"><td>Worker</td><td>Entered in Tally</td><td>Stock Journal No.</td><td>Other Details</td></tr>
        ${sheet.tally_rows.map(t => `<tr><td>${esc(t.worker_name || '')}</td><td>${t.entered_in_tally === 'yes' ? 'Yes' : t.entered_in_tally === 'no' ? 'No' : '\u2014'}</td><td>${esc(t.stock_journal_no || '\u2014')}</td><td>${esc(t.other_details || '')}</td></tr>`).join('')}
      </tbody>
    </table>
  </div>` : ''}

  ${signoffHtml([['Prepared By', sheet.created_by_name], ['Store Incharge', ''], ['Production Head', '']], sheet.remarks)}

  ${photos.length ? `
  <div class="cl-photos">
    ${tabHtml('layers', `Cutting List (${photos.length})`, '58mm')}
    ${photos.map(u => `<div class="cl-photo"><img src="${esc(u)}" alt="Cutting list" /></div>`).join('')}
  </div>` : ''}`;

  const extraCss = `
    .items .n { text-align: right; white-space: nowrap; }
    .items .b { font-weight: 800; }
    .items.cft td { font-size: 8pt; }
    .sum-wrap { break-inside: avoid; }
    table.sumt { width: 100%; border-collapse: collapse; background: #fff; border: .3mm solid #ddd5ca; border-top: 0; break-inside: avoid; }
    .sumt td { border: .3mm solid #ddd5ca; padding: 2mm 3mm; font-size: 8.6pt; }
    .sumt .n { text-align: right; white-space: nowrap; width: 40mm; }
    .sumt tr.strong td { background: #f6f1ea; }
    .sumt tr.net td { background: #2b2a27; color: #fff; font: 800 10.5pt Montserrat, Inter, sans-serif; }
    .sumt tr.net.excess td { background: #2f6b3a; }
    .sumt tr.net.shortage td { background: #a3342b; }
    .sumt.tally td { font-size: 8pt; }
    .sumt.tally tr.th td { background: #f6f1ea; font-weight: 700; font-size: 7.4pt; }
    .formula { font-size: 6.8pt; color: #8a8478; font-style: italic; margin-top: 1.5mm; }
    .cl-photos { margin-top: 5mm; }
    .cl-photo { background: #fff; border: .3mm solid #ddd5ca; border-top: 0; padding: 3mm; text-align: center; break-inside: avoid; }
    .cl-photo + .cl-photo { border-top: .3mm solid #ddd5ca; margin-top: 3mm; }
    .cl-photo img { max-width: 100%; max-height: 205mm; object-fit: contain; display: block; margin: 0 auto; }
  `;

  return buildMozDocument({ docTitle: `${sheet.sheet_no || 'CFT'} — CFT Sheet`, body, settings, origin, extraCss });
}
